import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Phone, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { useCheckin } from "../context/CheckinContext";
import { lookupParent, logNoticePresented, apiError } from "../lib/api";
import { BrandWordmark } from "../components/Brand";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { LegalTextModal } from "../components/LegalTextModal";
import { PhoneInput } from "../components/PhoneInput";
import {
  PRIVACY_NOTICE,
  PRIVACY_NOTICE_LINK_TEXT,
  PRIVACY_NOTICE_SENTENCE_PREFIX,
  PRIVACY_NOTICE_SENTENCE_SUFFIX,
  LEGAL_VERSIONS,
} from "../legal";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

export default function ParentPhone() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { update, reset } = useCheckin();
  const [country, setCountry] = useState("TR");
  const [national, setNational] = useState("");
  const [e164, setE164] = useState("");
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [matches, setMatches] = useState(null);

  const openNotice = () => {
    setNoticeOpen(true);
    // Audit log: which Aydınlatma Metni version was presented and when.
    logNoticePresented(e164 || national || null, LEGAL_VERSIONS.privacy_notice).catch(() => {});
  };

  const applyMatch = (m) => {
    update({ parent: m.parent, children: m.children, needsConsent: !!m.needs_consent });
    navigate("/parent/select");
  };

  const handleContinue = async () => {
    if (!valid || !e164) return toast.error(t("invalidPhoneForCountry"));
    setLoading(true);
    try {
      reset();
      const res = await lookupParent(e164);
      if (res.ambiguous) {
        setMatches(res.matches || []);
        return;
      }
      if (res.found) {
        update({ parent: res.parent, children: res.children, needsConsent: !!res.needs_consent });
        navigate("/parent/select");
      } else {
        update({ parent: { phone_number: e164 } });
        navigate("/parent/register");
      }
    } catch (e) {
      toast.error(apiError(e) || t("somethingWentWrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen lf-grain bg-[#FFEEBC]" data-testid="parent-phone-page">
      <header className="mx-auto flex max-w-3xl items-center justify-between px-5 py-6">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-bold text-[#26422A] hover:bg-[#26422A]/10"
        >
          <ArrowLeft className="h-4 w-4" /> {t("back")}
        </button>
        <LanguageSwitcher />
      </header>

      <main className="mx-auto flex max-w-md flex-col items-center px-5 pt-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full rounded-3xl border-2 border-[#26422A]/40 bg-[#FFFFFF] p-8 shadow-xl"
        >
          <div className="mb-6 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#26422A]">
              <Phone className="h-8 w-8 text-[#FFEEBC]" />
            </div>
          </div>
          <h1 className="text-center font-display text-3xl font-black text-[#26422A]">
            {t("welcome")}
          </h1>
          <p className="mt-2 text-center text-sm font-medium text-[#26422A]">
            {t("enterPhoneToCheckIn")}
          </p>

          <div className="mt-8 space-y-2">
            <Label htmlFor="phone" className="font-bold text-[#26422A]">
              {t("phoneNumber")}
            </Label>
            <PhoneInput
              testid="phone"
              country={country}
              setCountry={setCountry}
              national={national}
              setNational={setNational}
              onChange={(val, isValid) => { setE164(val); setValid(isValid); }}
            />
            <p className="text-xs text-[#26422A]">{t("phoneLookupHint")}</p>
          </div>

          <p className="mt-4 text-xs leading-relaxed text-[#26422A]/80" data-testid="privacy-notice-line">
            {PRIVACY_NOTICE_SENTENCE_PREFIX}
            <span
              role="button"
              tabIndex={0}
              data-testid="open-privacy-notice-btn"
              onClick={openNotice}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openNotice(); } }}
              className="cursor-pointer font-semibold text-[#26422A] underline underline-offset-2 hover:opacity-70"
            >
              {PRIVACY_NOTICE_LINK_TEXT}
            </span>
            {PRIVACY_NOTICE_SENTENCE_SUFFIX}
          </p>

          <Button
            data-testid="phone-continue-btn"
            onClick={handleContinue}
            disabled={loading}
            className="mt-8 h-14 w-full rounded-xl bg-[#26422A] text-base font-bold text-[#FFEEBC] hover:bg-[#1B3320]"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("continue")}
          </Button>
        </motion.div>
      </main>

      <LegalTextModal open={noticeOpen} onOpenChange={setNoticeOpen} doc={PRIVACY_NOTICE} testid="privacy-notice-modal" />

      <Dialog open={!!matches} onOpenChange={(v) => { if (!v) setMatches(null); }}>
        <DialogContent className="rounded-3xl border-2 border-[#26422A]/40 bg-[#FFFFFF]" data-testid="profile-chooser-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#26422A]">
              {t("selectYourProfile")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#26422A]/80">{t("multipleProfilesFound")}</p>
          <div className="mt-2 space-y-2">
            {(matches || []).map((m, i) => (
              <button
                key={m.parent.id}
                data-testid={`profile-option-${i}`}
                onClick={() => applyMatch(m)}
                className="flex w-full items-center gap-3 rounded-2xl border-2 border-[#26422A]/40 bg-white p-4 text-left hover:bg-[#26422A]/5"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FFEEBC]">
                  <Users className="h-5 w-5 text-[#26422A]" />
                </div>
                <div className="min-w-0">
                  <div className="font-display text-base font-bold text-[#26422A]">
                    {m.parent.name} {m.parent.surname}
                  </div>
                  <div className="truncate text-xs text-[#26422A]/70">
                    {m.children.length} {t("children")}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
